import { Link, useLocation } from "@tanstack/react-router";
import { AnimatePresence, motion } from "framer-motion";
import { ChevronDown, Menu, X } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { chrome } from "@/content/chrome";
import { localeCookie } from "@/i18n/detect";
import {
  type Locale,
  localeMeta,
  localePath,
  locales,
  stripLocalePrefix,
} from "@/i18n/locales";
import { cn } from "@/lib/utils";

type NavbarProps = {
  locale: Locale;
};

const navLinks = [
  { href: "/app", key: "app" },
  { href: "/travel", key: "travel" },
  { href: "/community", key: "community" },
  { href: "/about", key: "about" },
  { href: "/media", key: "media" },
  { href: "/blog", key: "blog" },
] as const;

function normalizeNavPath(path: string): string {
  return path.replace(/\/+$/, "") || "/";
}

function isNavLinkActive(href: string, currentPath: string): boolean {
  const current = normalizeNavPath(currentPath);
  const target = normalizeNavPath(href);
  return current === target || current.startsWith(`${target}/`);
}

function navLinkClassName(active: boolean, mobile = false): string {
  if (mobile) {
    return cn(
      "block w-full border-b py-2 text-left text-base font-semibold uppercase tracking-wide last:border-0",
      active
        ? "border-primary/30 bg-primary/5 text-primary"
        : "border-border/40 text-[#1a3a5c]",
    );
  }
  return cn(
    "relative pb-0.5 text-sm font-semibold uppercase tracking-wide transition-colors",
    active
      ? "text-primary after:absolute after:inset-x-0 after:-bottom-1 after:h-0.5 after:rounded-full after:bg-primary"
      : "text-[#1a3a5c]/70 hover:text-[#1a3a5c]",
  );
}

export function Navbar({ locale }: NavbarProps) {
  const [mobileOpen, setMobileOpen] = useState(false);
  const [qrOpen, setQrOpen] = useState(false);
  const qrRef = useRef<HTMLDivElement>(null);
  const location = useLocation();
  const copy = chrome[locale];
  const currentPath = stripLocalePrefix(location.pathname);

  function switchLocale(next: Locale) {
    if (typeof document !== "undefined") {
      // biome-ignore lint/suspicious/noDocumentCookie: SameSite=Lax locale cookie
      document.cookie = localeCookie(next);
    }
  }

  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (qrRef.current && !qrRef.current.contains(event.target as Node)) {
        setQrOpen(false);
      }
    }
    if (qrOpen) document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [qrOpen]);

  return (
    <nav className="fixed inset-x-0 top-0 z-50">
      <div className="hidden bg-[#1a3a5c] text-xs text-white/70 md:block">
        <div className="mx-auto flex h-8 max-w-6xl items-center justify-between px-5">
          <span className="font-medium tracking-wide">{copy.nav.tagline}</span>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button
                type="button"
                className="flex items-center gap-1 text-white/70 transition-colors hover:text-white"
              >
                <span>{localeMeta[locale].flag}</span>
                <span className="font-medium">{localeMeta[locale].label}</span>
                <ChevronDown className="h-3 w-3" />
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="min-w-[140px]">
              {locales.map((next) => (
                <DropdownMenuItem key={next} asChild>
                  <Link
                    to={localePath(next, currentPath)}
                    onClick={() => switchLocale(next)}
                    className={cn(
                      "flex cursor-pointer items-center gap-2",
                      next === locale && "font-semibold text-primary",
                    )}
                  >
                    <span>{localeMeta[next].flag}</span>
                    <span>{localeMeta[next].label}</span>
                  </Link>
                </DropdownMenuItem>
              ))}
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>

      <div
        className="border-b border-primary/10 bg-white/90 shadow-sm backdrop-blur-2xl"
        style={{ boxShadow: "0 1px 20px rgba(14, 165, 201, 0.06)" }}
      >
        <div className="mx-auto flex h-16 max-w-6xl items-center justify-between px-5">
          <Link
            to={localePath(locale)}
            className="flex items-center gap-2.5"
            onClick={() => setMobileOpen(false)}
          >
            <img
              src="/icon-ui.webp"
              alt=""
              width={192}
              height={192}
              className="h-9 w-9 shrink-0 rounded-[10px] shadow-sm ring-1 ring-[#1a3a5c]/10"
            />
            <div className="font-heading text-lg font-bold tracking-tight text-[#1a3a5c]">
              Vaxy
            </div>
          </Link>

          <div className="hidden items-center gap-8 md:flex">
            {navLinks.map((item) => (
              <Link
                key={item.href}
                to={localePath(locale, item.href)}
                className={navLinkClassName(
                  isNavLinkActive(item.href, currentPath),
                )}
              >
                {copy.nav.links[item.key]}
              </Link>
            ))}
          </div>

          <div
            className="relative hidden items-center gap-3 md:flex"
            ref={qrRef}
          >
            <Button
              className="rounded-sm px-6 font-semibold"
              onClick={() => setQrOpen((open) => !open)}
            >
              {copy.nav.getApp}
            </Button>
            <AnimatePresence>
              {qrOpen ? (
                <motion.div
                  initial={{ opacity: 0, y: 8, scale: 0.95 }}
                  animate={{ opacity: 1, y: 0, scale: 1 }}
                  exit={{ opacity: 0, y: 8, scale: 0.95 }}
                  transition={{ duration: 0.18 }}
                  className="absolute right-0 top-full z-50 mt-3 flex w-56 flex-col items-center rounded-2xl border border-border bg-white p-5 shadow-2xl"
                >
                  <img
                    src="/images/qr.webp"
                    alt={copy.nav.qrAlt}
                    width={111}
                    height={111}
                    className="h-40 w-40 object-contain"
                  />
                  <p className="mt-3 text-center text-sm font-semibold text-[#1a3a5c]">
                    {copy.nav.scanQr}
                  </p>
                </motion.div>
              ) : null}
            </AnimatePresence>
          </div>

          <button
            type="button"
            className="p-2 text-[#1a3a5c] md:hidden"
            onClick={() => setMobileOpen((open) => !open)}
            aria-label="Toggle menu"
          >
            {mobileOpen ? (
              <X className="h-5 w-5" />
            ) : (
              <Menu className="h-5 w-5" />
            )}
          </button>
        </div>
      </div>

      <AnimatePresence>
        {mobileOpen ? (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            className="overflow-hidden border-b border-border bg-white md:hidden"
          >
            <div className="space-y-3 px-5 py-4">
              {navLinks.map((item) => (
                <Link
                  key={item.href}
                  to={localePath(locale, item.href)}
                  className={navLinkClassName(
                    isNavLinkActive(item.href, currentPath),
                    true,
                  )}
                  onClick={() => setMobileOpen(false)}
                >
                  {copy.nav.links[item.key]}
                </Link>
              ))}
              <div className="flex flex-wrap gap-2 pt-2">
                {locales.map((next) => (
                  <Link
                    key={next}
                    to={localePath(next, currentPath)}
                    onClick={() => {
                      switchLocale(next);
                      setMobileOpen(false);
                    }}
                    className={cn(
                      "flex items-center gap-1.5 border px-3 py-1.5 text-sm font-medium",
                      next === locale
                        ? "border-primary bg-primary text-primary-foreground"
                        : "border-border bg-white text-foreground hover:bg-secondary",
                    )}
                  >
                    <span>{localeMeta[next].flag}</span>
                    <span>{next.toUpperCase()}</span>
                  </Link>
                ))}
              </div>
              <Button
                className="mt-2 w-full rounded-sm font-semibold"
                onClick={() => setQrOpen((open) => !open)}
              >
                {copy.nav.getApp}
              </Button>
              {qrOpen ? (
                <div className="flex flex-col items-center rounded-2xl border border-border p-5">
                  <img
                    src="/images/qr.webp"
                    alt={copy.nav.qrAlt}
                    width={111}
                    height={111}
                    className="h-40 w-40 object-contain"
                  />
                  <p className="mt-3 text-center text-sm font-semibold text-[#1a3a5c]">
                    {copy.nav.scanQr}
                  </p>
                </div>
              ) : null}
            </div>
          </motion.div>
        ) : null}
      </AnimatePresence>
    </nav>
  );
}
